import { useState } from 'react'

import {
  Box,
  Icon,
  Text,
  TogglerCheckbox,
  Tooltip,
  useToggle,
  zIndex,
} from '@factorialco/deprecated-design-system'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'

import SignersList from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings/SignersList'
import SignersPopover from 'modules/documents/components/BulkFiles/_steps/EsignatureSettings/SignersPopover'

export interface SettingsValues {
  newEsignatureRequest: boolean
  newAdditionalSignersAccessIds: string[]
  newSignaturePlacement?: boolean
  newSequentialSigning?: boolean
  hidden?: boolean
}

function SequentialSigningInfoIcon() {
  const [hover, setHover] = useState(false)

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Tooltip.Alt
        attachment="bottom center"
        textAlign="center"
        label={i18n.t('documents.modal.esignature.modal.sequential_signing_tooltip')}
        open={hover}
      >
        <Icon.Medium icon="info-circle-line" color="grey800" />
      </Tooltip.Alt>
    </span>
  )
}

interface Props {
  settings: SettingsValues
  onChange: (value: SettingsValues) => void
  showRecipient?: boolean
  showSignaturePlacement?: boolean
  showSequentialSigning?: boolean
  defaultSigner?: string
  signableFiles?: boolean
}

const EsignatureSettings = ({
  settings: {
    newEsignatureRequest,
    newAdditionalSignersAccessIds,
    newSignaturePlacement = false,
    newSequentialSigning = false,
    hidden = false,
  },
  onChange,
  showRecipient = true,
  showSignaturePlacement = false,
  showSequentialSigning: showSequentialSigningProp = false,
  defaultSigner,
  signableFiles = true,
}: Props) => {
  const noSignersAdded = newAdditionalSignersAccessIds.length === 0
  const showSignersList = newAdditionalSignersAccessIds.length > 0 || showRecipient
  const { has_dev_esignature_sequential_signing } = useFeatures([
    'dev_esignature_sequential_signing',
  ])
  const showSequentialSigning = showSequentialSigningProp && has_dev_esignature_sequential_signing
  const totalSigners = newAdditionalSignersAccessIds.length + (showRecipient ? 1 : 0)
  const canEnableSequentialSigning = totalSigners >= 2
  const showAdvancedSettings = showSignaturePlacement || showSequentialSigning
  const [openEsignatureRequestTooltip, toggleEsignatureRequestTooltip] = useToggle()
  const [openSequentialSigningTooltip, toggleSequentialSigningTooltip] = useToggle()

  const addAdditionalSigners = (additionalSigners: string[]) => {
    onChange({
      newEsignatureRequest,
      newAdditionalSignersAccessIds: additionalSigners,
      newSignaturePlacement,
      newSequentialSigning,
    })
  }

  const removeRecipient = () => {
    onChange({
      newEsignatureRequest: false,
      newAdditionalSignersAccessIds: [],
      newSignaturePlacement,
      newSequentialSigning: false,
    })
  }

  const removeSigner = (idToRemove: string) => {
    const newAdditionalSigners = newAdditionalSignersAccessIds.filter(
      (signerId) => signerId !== idToRemove
    )
    const remainingTotalSigners = newAdditionalSigners.length + (showRecipient ? 1 : 0)
    onChange({
      newEsignatureRequest,
      newAdditionalSignersAccessIds: newAdditionalSigners,
      newSignaturePlacement,
      newSequentialSigning: remainingTotalSigners < 2 ? false : newSequentialSigning,
    })
  }

  const reorderSigners = (reorderedAccessIds: string[]) => {
    onChange({
      newEsignatureRequest,
      newAdditionalSignersAccessIds: reorderedAccessIds,
      newSignaturePlacement,
      newSequentialSigning,
    })
  }

  const tooltipAlert = !signableFiles
    ? i18n.t('documents.modal.esignature.modal.only_pdf_alert')
    : i18n.t('documents.modal.esignature.modal.only_visible_alert')

  return (
    <>
      <Box
        padding="s16"
        borderRadius={{ all: 'abs016' }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        style={{ zIndex: zIndex.omnibutton }}
        background="grey000White"
      >
        <Box flexDirection="column" gap="s16">
          <Box width="full" justifyContent="spaceBetween" flexDirection="row">
            <>
              <Text.Normal weight="semibold">
                {i18n.t('documents.modal.esignature.modal.title')}
              </Text.Normal>
            </>
            <>
              <Tooltip.Alt
                attachment="bottom center"
                open={(hidden || !signableFiles) && openEsignatureRequestTooltip}
                toggle={toggleEsignatureRequestTooltip}
                label={tooltipAlert}
              >
                <TogglerCheckbox
                  value={newEsignatureRequest}
                  aria-label="esignature request"
                  onChange={(value) => {
                    const additionalSigners = [
                      ...(defaultSigner ? [defaultSigner] : []),
                      ...newAdditionalSignersAccessIds,
                    ]

                    onChange({
                      newEsignatureRequest: value,
                      newAdditionalSignersAccessIds: value ? additionalSigners : [],
                      newSignaturePlacement,
                      newSequentialSigning,
                    })
                  }}
                  disabled={hidden || !signableFiles}
                />
              </Tooltip.Alt>
            </>
          </Box>

          {newEsignatureRequest && showAdvancedSettings && (
            <Box justifyContent="spaceBetween" gap="s8" flexDirection="column">
              {showSignaturePlacement && (
                <TogglerCheckbox
                  value={newSignaturePlacement}
                  label={i18n.t('documents.beta.request_esignature_placement')}
                  onChange={(value) =>
                    onChange({
                      newEsignatureRequest,
                      newAdditionalSignersAccessIds,
                      newSignaturePlacement: value,
                      newSequentialSigning,
                    })
                  }
                  disabled={noSignersAdded}
                />
              )}
              {showSequentialSigning && (
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <Tooltip.Alt
                    attachment="bottom center"
                    open={!canEnableSequentialSigning && openSequentialSigningTooltip}
                    toggle={toggleSequentialSigningTooltip}
                    label={i18n.t(
                      'documents.modal.esignature.modal.sequential_signing_disabled_tooltip'
                    )}
                  >
                    <TogglerCheckbox
                      value={newSequentialSigning}
                      label={i18n.t('documents.modal.esignature.modal.sequential_signing')}
                      onChange={(value) =>
                        onChange({
                          newEsignatureRequest,
                          newAdditionalSignersAccessIds,
                          newSignaturePlacement,
                          newSequentialSigning: value,
                        })
                      }
                      disabled={!canEnableSequentialSigning}
                    />
                  </Tooltip.Alt>
                  <SequentialSigningInfoIcon />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {newEsignatureRequest && (
        <>
          <Box
            background="grey100"
            padding="s24"
            paddingBottom="s16"
            gap="s16"
            borderRadius={{
              bottomLeft: 'abs016',
              bottomRight: 'abs016',
            }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            style={{ marginTop: '-12px' }}
          >
            {showSignersList && (
              <>
                <SignersList
                  additionalSigners={newAdditionalSignersAccessIds}
                  removeRecipient={removeRecipient}
                  removeSigner={removeSigner}
                  showRecipient={showRecipient}
                  sequentialSigning={newSequentialSigning}
                  onReorder={reorderSigners}
                />
              </>
            )}
            <SignersPopover
              additionalSigners={newAdditionalSignersAccessIds}
              addAdditionalSigners={addAdditionalSigners}
            />
          </Box>
        </>
      )}
    </>
  )
}

export default EsignatureSettings
